import { DataGridTable, Tabs } from 'components'
import React, { useCallback, useEffect, useState } from 'react'
import {
  activeVehiclesActionList,
  pausedVehiclesActionList,
  getCarrierHeartbeatVehiclesColumns,
} from './carrierHeartbeatVehicleTableData'
import CarrierHeartbeatTableToolbar from './carrierHeartbeatTableToolbar'
import { Box, Paper, Typography } from '@mui/material'
import { MappedHeartbeatVehicle } from 'interfaces/ICarrierHeartbeat'
import { GridRowHeightParams, GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid'
import strings from 'strings'
import { exportTableAsCSV } from 'tools/helper'

const t = strings.TABLE_DEFAULTS
const e = strings.CSV_EXPORT
interface ICarrierHeartbeatVehiclesTableProps {
  vehiclesList: MappedHeartbeatVehicle[]
  toolbarActions: any
  loading?: boolean
  reloadVehicles: (active: number) => void
  pauseVehicle: (
    identificationNoList: string[],
    assetEldIdList: (number | string)[],
    isActive: number
  ) => void
  removeVehicle: (identificationNoList: string[], isActive: number) => void
  addVehicle: (identificationNoList: string[], isActive: number) => void
  showDialog: (config: any, contentNode?: React.ReactNode) => void
  periodFilter: string
  isReadonlyDashboard: boolean
}

const heartbeatVehiclesTabs = [
  {
    label: 'Active Vehicles',
  },
  {
    label: 'Paused Vehicles',
  },
]

const CarrierHeartbeatVehiclesTable: React.FC<ICarrierHeartbeatVehiclesTableProps> =
  ({
    vehiclesList,
    toolbarActions,
    loading,
    reloadVehicles,
    pauseVehicle,
    removeVehicle,
    addVehicle,
    showDialog,
    periodFilter,
    isReadonlyDashboard
  }) => {
    const [tab, setTab] = useState(0)
    const [filteredVehicles, setFilteredVehicles] =
      useState<MappedHeartbeatVehicle[]>(vehiclesList)
    const [hasSelectedRows, setHasSelectedRows] = useState(false)

    // Filter states
    const [policyFilter, setPolicyFilter] = useState('any')
    const [telematicsFilter, setTelematicsFilter] = useState('any')
    const [stateFilter, setStateFilter] = useState('any')
    const apiRef = useGridApiRef()

    // For comma separated values only
    const createGetEstimatedRowHeight = useCallback(
      (propertyName: string) => 
        ({ model, densityFactor }: GridRowHeightParams): number | null => {
          const propertyValue = model?.[propertyName]
  
          if (propertyValue && typeof propertyValue === 'string') {
            const baseHeight = t.DEFAULT_ROW_HEIGHT
            const providerCount = propertyValue.split(', ').length
            const heightPerProvider = t.DEFAULT_ELD_PROVIDER_ROW_HEIGHT
            const padding = t.DEFAULT_CELL_PADDING
  
            const calculatedHeight = providerCount * heightPerProvider + padding
            return Math.max(baseHeight, calculatedHeight) * densityFactor
          }
          return null
        },
      [vehiclesList]
    )

    useEffect(() => {
      reloadVehicles(isActive)
      setHasSelectedRows(false)
      setSelectedRowsData([])
      if (apiRef.current) {
        apiRef.current.setRowSelectionModel([])
      }
    }, [tab])

    useEffect(() => {
      const applyFilters = () => {
        let filtered = [...vehiclesList]

        // Policy filter
        if (policyFilter !== 'any') {
          filtered = filtered.filter((vehicle) =>
            policyFilter === 'true' ? vehicle.policyList : !vehicle.policyList
          )
        }

        // Telematics filter
        if (telematicsFilter !== 'any') {
          filtered = filtered.filter((vehicle) =>
            telematicsFilter === 'true'
              ? vehicle.telematics
              : !vehicle.telematics
          )
        }

        // Combined State and Period filter
        if (stateFilter !== 'any' || periodFilter !== 'any') {
          filtered = filtered.filter((vehicle) => {
            const isMoving = stateFilter === 'true'
            const isNotMoving = stateFilter === 'false'

            // If only state filter is applied (no period filter)
            if (stateFilter !== 'any' && periodFilter === 'any') {
              if (isMoving) {
                return (
                  vehicle.lastMovement !== null &&
                  vehicle.lastMovement !== undefined &&
                  vehicle.lastMovement !== t.DASH_VALUE
                )
              } else {
                return (
                  !vehicle.lastMovement || vehicle.lastMovement === t.DASH_VALUE
                )
              }
            }

            // If only period filter is applied (no state filter)
            if (stateFilter === 'any' && periodFilter !== 'any') {
              switch (periodFilter) {
                case '30':
                  return vehicle.movingIn30Days
                case '60':
                  return vehicle.movingIn60Days
                case '90':
                  return vehicle.movingIn90Days
                default:
                  return true
              }
            }

            // If both filters are applied - combine them logically
            if (stateFilter !== 'any' && periodFilter !== 'any') {
              switch (periodFilter) {
                case '30':
                  return isMoving
                    ? vehicle.movingIn30Days
                    : !vehicle.movingIn30Days
                case '60':
                  return isMoving
                    ? vehicle.movingIn60Days
                    : !vehicle.movingIn60Days
                case '90':
                  return isMoving
                    ? vehicle.movingIn90Days
                    : !vehicle.movingIn90Days
                default:
                  return true
              }
            }

            return true
          })
        }

        setFilteredVehicles(filtered)
      }
      applyFilters()
    }, [
      vehiclesList,
      policyFilter,
      telematicsFilter,
      stateFilter,
      periodFilter,
    ])

    const isActive = tab === 0 ? 1 : 0

    const handleExport = () => {
      const csvString = apiRef.current.getDataAsCsv()
      exportTableAsCSV(csvString, e.CARRIER_HEARTBEAT_CSV_EXPORT_NAME)
    }

    const tableMenuItems = [
      {
        label: e.DOWNLOAD_AS_CSV,
        key: 'export',
        action: () => {
          handleExport()
        },
      },
    ]
    const [selectedRowsData, setSelectedRowsData] = useState<
      MappedHeartbeatVehicle[]
    >([])

    const handleCheckboxSelection = (selections: GridRowSelectionModel) => {
      const selectedRows = selections.map((rowId) =>
        apiRef.current.getRow(rowId)
      )
      setSelectedRowsData(selectedRows)
      setHasSelectedRows(selections.length > 0)
    }

    const handleActions = (id: string | number, name: string) => {
      const vehicle = filteredVehicles.find((v) => v.rowId === id)
      if (!vehicle) return

      const action = name?.toLowerCase()
      if (action === 'add') {
        addVehicle(
          vehicle.identificationNo ? [vehicle.identificationNo] : [],
          isActive
        )
      } else if (action === 'pause') {
        showDialog(
          {
            type: 'warning',
            title: 'Pause Vehicle',
            body1: '',
            action: async () => {
              pauseVehicle(
                vehicle.identificationNo ? [vehicle.identificationNo] : [],
                vehicle.assetEldId ? [vehicle.assetEldId] : [],
                isActive
              )
            },
            primaryBtnProps: {
              label: 'Pause Vehicle',
              color: 'primary',
            },
          },
          togglePauseVehicleDialogBody(vehicle)
        )
      } else if (action === 'resume') {
        showDialog(
          {
            type: 'warning',
            title: 'Reactivate Vehicle',
            body1: '',
            action: async () => {
              pauseVehicle(
                vehicle.identificationNo ? [vehicle.identificationNo] : [],
                vehicle.assetEldId ? [vehicle.assetEldId] : [],
                isActive
              )
            },
            primaryBtnProps: {
              label: 'Reactivate Vehicle',
              color: 'primary',
            },
          },
          togglePauseVehicleDialogBody(vehicle)
        )
      } else if (action === 'remove') {
        showDialog(
          {
            type: 'delete',
            title: 'Remove from Policy',
            body1: '',
            action: async () => {
              removeVehicle(
                vehicle.identificationNo ? [vehicle.identificationNo] : [],
                isActive
              )
            },
            primaryBtnProps: {
              label: 'Remove Vehicle',
              color: 'error',
            },
          },
          removeVehicleDialogBody(vehicle)
        )
      }
    }

    const togglePauseVehicleDialogBody = (vehicle: MappedHeartbeatVehicle) => {
      const validAssetEldId =
        vehicle.assetEldId === t.DASH_VALUE ? '' : vehicle.assetEldId
      return (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {vehicle.pullingEnabled
              ? 'You are about to pause the vehicle'
              : 'You are about to reactivate the vehicle'}
            : <strong>{validAssetEldId}</strong> (VIN:{' '}
            <strong>{vehicle.identificationNo}</strong>).
          </Typography>
          <Typography variant="body1">
            {vehicle.pullingEnabled
              ? 'This will stop telematics data collection for this vehicle.'
              : 'This will resume telematics data collection for this vehicle.'}
          </Typography>
        </>
      )
    }
    const removeVehicleDialogBody = (vehicle: MappedHeartbeatVehicle) => {
      const validAssetEldId =
        vehicle.assetEldId === t.DASH_VALUE ? '' : vehicle.assetEldId
      return (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to remove vehicle: <strong>{validAssetEldId}</strong>{' '}
            (VIN: <strong>{vehicle.identificationNo}</strong>).
          </Typography>
          <Typography variant="body1">
            This will completely remove the vehicle from your policy list. This
            action cannot be undone without re-uploading the vehicle through CSV
            or manually adding the vehicle again.
          </Typography>
        </>
      )
    }

    const tabsWithCount = [
      {
        label: `Active Vehicles`,
      },
      {
        label: 'Paused Vehicles',
      },
    ]

    return (
      <Box sx={{ px: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Fleet Vehicles {isReadonlyDashboard ? ' (Read Only)' : ''}
        </Typography>
        <Paper
          sx={{
            boxShadow: 'none',
            border: '1px solid #E9E9E9',
            borderRadius: 2,
          }}
        >
          <Tabs
            tabData={tabsWithCount}
            value={tab}
            id={heartbeatVehiclesTabs[tab].label}
            handleChange={(_e, val) => setTab(val)}
            sxContainer={{
              borderBottom: '1px solid #E9E9E9',
            }}
            sxTabs={{
              minHeight: '44px',
              '& .MuiTabs-indicator': {
                backgroundColor: '#0088E1',
              },
            }}
            sxTab={{
              minHeight: '44px',
              fontWeight: 500,
              color: '#6B7280',
              '&.Mui-selected': {
                color: '#0088E1',
                fontWeight: 600,
              },
            }}
          />
          <Box>
            <DataGridTable
              apiRef={apiRef}
              showTitle={false}
              data={filteredVehicles}
              columns={getCarrierHeartbeatVehiclesColumns(periodFilter, isReadonlyDashboard)}
              columnVisibilityModel={{
                rowId: false,
              }}
              checkboxSelection={!isReadonlyDashboard}
              totalCount={filteredVehicles.length}
              multiSearchFilter={false}
              components={{
                toolbar: (props) => (
                  <CarrierHeartbeatTableToolbar
                    {...props}
                    handlers={toolbarActions}
                    policyFilter={policyFilter}
                    setPolicyFilter={setPolicyFilter}
                    telematicsFilter={telematicsFilter}
                    setTelematicsFilter={setTelematicsFilter}
                    stateFilter={stateFilter}
                    setStateFilter={setStateFilter}
                    periodFilter={periodFilter}
                    setPeriodFilter={toolbarActions.setPeriodFilter}
                    isActive={isActive}
                    isBulkAction={hasSelectedRows}
                    selectedRowsData={selectedRowsData}
                    isReadonlyDashboard={isReadonlyDashboard}
                  />
                ),
              }}
              actionList={
                !isReadonlyDashboard ? tab === 0 ? activeVehiclesActionList : pausedVehiclesActionList : []
              }
              handleActions={handleActions}
              handleCheckboxSelection={handleCheckboxSelection}
              getRowId={(row) => row.rowId}
              reduceHeight={350}
              loading={loading}
              menuList={tableMenuItems}
              useDynamicRowHeight={true}
              getEstimatedRowHeight={createGetEstimatedRowHeight('eldProviderName')}
            />
          </Box>
        </Paper>
      </Box>
    )
  }

export default CarrierHeartbeatVehiclesTable
